/* eslint-disable max-len */
import { useAuthInfo, useRedirectFunctions } from "@propelauth/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { formatISO, fromUnixTime } from "date-fns";
import { useCurrentUser, useCurrentWorkspace } from "../../hooks/QueryHooks";
import { getWorkspace } from "../../services/workspace/workspaceEndpoints";
import notify from "../../utils/notify";
import LoadWorkspace from "./components/LoadWorkspace";
import { useDefaultWorkspaceId } from "./useDefaultWorkspaceId";

// Constants for polling configuration
const WORKSPACE_POLLING_CONFIG = {
  MAX_ATTEMPTS: 10,
  INITIAL_DELAY: 1000, // 1 second
  MAX_DELAY: 30000, // 30 seconds
  MULTIPLIER: 1.5,
  TIMEOUT: 300000, // 5 minutes total timeout
};

const Root = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [pollingState, setPollingState] = useState<{
    isPolling: boolean;
    attempts: number;
    startTime: number;
  }>({ isPolling: false, attempts: 0, startTime: 0 });
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const params = useParams();
  const authInfo = useAuthInfo();
  const authRoutes = useRedirectFunctions();
  // Default-workspace id; re-read on every navigation so the workspace switcher
  // (which writes localStorage then navigates) takes effect. See the hook.
  const { heDef, setDefaultWorkspace, clearDefaultWorkspace } = useDefaultWorkspaceId();
  const ws: any = authInfo?.orgHelper?.getOrgs();

  useCurrentUser({});

  // DB is the source of truth for setup_complete. PropelAuth org_metadata is a
  // cached copy that only updates on PUT /workspace + a token refresh, so it can
  // lag (e.g. right after email-first onboarding) and wrongly bounce users back
  // to onboarding. We read the DB value here and use org_metadata only as a
  // fallback when the DB row isn't available.
  const currentWorkspaceQuery = useCurrentWorkspace({
    workspaceId: heDef,
    enabled: !authInfo.loading && !!heDef,
    retry: false,
  });
  const dbSetupComplete: boolean | undefined = (currentWorkspaceQuery.data as any)?.setup_complete;
  // "Ready" means we have a definitive DB answer (fetched or errored), or there
  // is no default workspace to fetch yet. Note: in react-query v4 a *disabled*
  // query reports isLoading=true, so we must not gate on isLoading.
  const dbWorkspaceReady = !heDef || currentWorkspaceQuery.isFetched || currentWorkspaceQuery.isError;

  // Helper function to identify user to HyperEngage analytics
  const identifyUserToAnalytics = useCallback((workspace: any) => {
    if (
      import.meta.env.VITE_HE_WORKSPACE_KEY &&
      import.meta.env.VITE_HE_DATA_KEY
    ) {
      window?.hyperengage("identify_account", {
        account_id: workspace?.orgId,
        traits: {
          name: workspace?.orgName,
          unique_identifier: workspace?.orgMetadata?.unique_identifier,
          setup_complete: workspace?.orgMetadata?.setup_complete,
          mrr: 0,
          is_trial: true,
        },
      });
      if (authInfo.user) {
        window?.hyperengage("identify_user", {
          user_id: authInfo.user.userId,
          traits: {
            created_at: formatISO(fromUnixTime(authInfo.user.createdAt)),
            name: `${authInfo.user.firstName} ${authInfo.user.lastName}`,
            email: authInfo.user.email,
            first_name: authInfo.user.firstName,
            last_name: authInfo.user.lastName,
            avatar_url: authInfo.user.pictureUrl,
            properties: authInfo.user?.properties,
            can_create_orgs: authInfo.user?.canCreateOrgs,
            mfa_enabled: authInfo.user?.mfaEnabled,
          },
        });
      }
    }
  }, [authInfo.user]);

  // Workspace polling function with exponential backoff
  const pollForWorkspace = useCallback(async (orgId: string, startTime: number, attempt: number = 1) => {
    console.log(`Polling attempt ${attempt} for workspace ${orgId}`);
    
    if (attempt > WORKSPACE_POLLING_CONFIG.MAX_ATTEMPTS) {
      setLoading(false);
      setPollingState({ isPolling: false, attempts: 0, startTime: 0 });
      notify({
        heading: "Workspace Setup Timeout",
        messageList: [
          "We're still setting up your workspace. Please refresh the page or contact support if this continues.",
        ],
        type: "failure",
      });
      return;
    }

    // Check if we've exceeded the total timeout
    const elapsed = Date.now() - startTime;
    if (elapsed > WORKSPACE_POLLING_CONFIG.TIMEOUT) {
      setLoading(false);
      setPollingState({ isPolling: false, attempts: 0, startTime: 0 });
      notify({
        heading: "Workspace Setup Timeout",
        messageList: [
          "Workspace setup is taking longer than expected. Please contact support.",
        ],
        type: "failure",
      });
      return;
    }

    try {
      const workspaceData = await getWorkspace({ workspace_id: orgId });

      if (workspaceData?.data?.id) {
        // Success! Workspace found
        console.log(`Workspace found after ${attempt} attempts!`);
        setDefaultWorkspace(workspaceData.data.id);

        // Use data from API response directly to navigate,
        // since authInfo closure is stale after refreshAuthInfo()
        const uniqueId = workspaceData.data.unique_identifier;
        const setupComplete = workspaceData.data.setup_complete;

        // Refresh auth in background so subsequent renders have fresh data
        authInfo.refreshAuthInfo();

        setLoading(false);
        setPollingState({ isPolling: false, attempts: 0, startTime: 0 });

        // Navigate using the API response data (not stale authInfo)
        if (uniqueId) {
          if (!setupComplete) {
            navigate(`/onboarding/workspace/${uniqueId}`);
          } else {
            navigate(`/home/${uniqueId}/headquarters`);
          }
        }
        return;
      }
    } catch (error) {
      console.warn(`Workspace polling attempt ${attempt} failed:`, error);
    }

    // Calculate next delay with exponential backoff
    const delay = Math.min(
      WORKSPACE_POLLING_CONFIG.INITIAL_DELAY * (WORKSPACE_POLLING_CONFIG.MULTIPLIER ** (attempt - 1)),
      WORKSPACE_POLLING_CONFIG.MAX_DELAY
    );

    console.log(`Scheduling next attempt in ${delay}ms`);

    // Update polling state
    setPollingState(prev => ({ ...prev, attempts: attempt }));

    // Schedule next attempt
    pollingTimeoutRef.current = setTimeout(() => {
      pollForWorkspace(orgId, startTime, attempt + 1);
    }, delay);
  }, [authInfo, navigate, setDefaultWorkspace]);

  // Start workspace creation polling
  const startWorkspacePolling = useCallback((orgId: string) => {
    console.log(`Starting workspace polling for ${orgId}, current polling state:`, pollingState.isPolling);
    
    if (pollingState.isPolling) {
      console.log("Already polling, skipping...");
      return; // Already polling
    }
    
    const startTime = Date.now();
    
    setLoading(true);
    setPollingState({
      isPolling: true,
      attempts: 1,
      startTime,
    });
    
    pollForWorkspace(orgId, startTime, 1);
  }, [pollForWorkspace, pollingState.isPolling]);

  // Cleanup polling on unmount
  useEffect(() => () => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
  }, []);

  // Main navigation logic
  useEffect(() => {
    // Skip if still loading auth info
    if (authInfo.loading) {
      return;
    }

    // Skip for viewer routes
    if (location.pathname.includes("viewer")) {
      return;
    }

    // Skip if we're already on a workspace-specific route
    if (params?.workspaceId) {
      return;
    }

    // Skip if already polling for workspace
    if (pollingState.isPolling) {
      return;
    }

    const selectedWorkspace: any = authInfo.orgHelper?.getOrg(heDef) || undefined;

    // Handle setup completion refresh
    if (location?.state?.setupComplete) {
      setLoading(true);
      const refreshAuth = async () => {
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), 2000);
        }); // Brief delay
        await authInfo.refreshAuthInfo();
        setLoading(false);
      };
      refreshAuth();
      return;
    }

    // No organizations found - redirect to create org
    if (!ws?.length) {
      authRoutes.redirectToCreateOrgPage();
      return;
    }

    // Multiple workspaces but no default selected
    if (!selectedWorkspace && ws?.length > 1) {
      clearDefaultWorkspace();
      navigate("/workspaces");
      return;
    }

    // Single workspace case
    if (!selectedWorkspace && ws?.length === 1) {
      const workspace = ws[0];
      
      // Case 1: Workspace exists in PropelAuth but doesn't have unique_identifier yet
      // This means backend hasn't created the workspace data yet
      if (!workspace?.orgMetadata?.unique_identifier) {
        console.log("Workspace not yet created in backend, starting polling...");
        startWorkspacePolling(workspace.orgId);
        return;
      }

      // Case 2: Workspace has unique_identifier. Set it as the default and let
      // the re-render flow into the DB-backed selected-workspace branch below,
      // so the redirect decision is made off the DB value, not org_metadata.
      identifyUserToAnalytics(workspace);
      setDefaultWorkspace(workspace.orgId);
      return;
    }

    // Selected workspace exists but missing unique_identifier
    // This can happen if PropelAuth data is stale
    if (selectedWorkspace && !selectedWorkspace?.orgMetadata?.unique_identifier) {
      console.log("Selected workspace missing identifier, refreshing auth...");
      setLoading(true);
      const refreshAuth = async () => {
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), 2000);
        });
        await authInfo.refreshAuthInfo();
        setLoading(false);
      };
      refreshAuth();
      return;
    }

    // Selected workspace exists and has all required data
    if (selectedWorkspace?.orgMetadata?.unique_identifier) {
      identifyUserToAnalytics(selectedWorkspace);

      // Wait for the DB-backed workspace before deciding, so we don't bounce the
      // user to onboarding off a stale org_metadata flag. Falls back to
      // org_metadata only if the DB row is unavailable (e.g. fetch errored).
      if (!dbWorkspaceReady) return;
      const setupComplete = dbSetupComplete ?? selectedWorkspace?.orgMetadata?.setup_complete;

      if (!setupComplete) {
        navigate(`/onboarding/workspace/${selectedWorkspace.orgMetadata.unique_identifier}`);
      } else {
        navigate(`/home/${selectedWorkspace.orgMetadata.unique_identifier}/headquarters`);
      }
    }
  }, [authInfo.loading, location?.state?.setupComplete, location?.state?.refresh, heDef, ws, params?.workspaceId, authRoutes, navigate, identifyUserToAnalytics, startWorkspacePolling, dbWorkspaceReady, dbSetupComplete, setDefaultWorkspace, clearDefaultWorkspace]);

  if (authInfo.loading) {
    return (
      <>
        <Outlet />
        <LoadWorkspace />
      </>
    );
  }

  return (
    <>
      <Outlet />
      {loading && <LoadWorkspace />}
    </>
  );
};

export default Root;
